import React, { useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { toastService } from '../../services/toast.service'
import { parseBackendErrors } from '../../utils/errorHandler'
import './EditDialog.scss'

const parseValidationErrors = (validationErrors: string[]): Record<string, string> => {
  const fieldErrors: Record<string, string> = {}
  for (const err of validationErrors) {
    const colonIndex = err.indexOf(':')
    if (colonIndex === -1) {
      fieldErrors._general = err
      continue
    }
    const field = err.slice(0, colonIndex)
    const message = err.slice(colonIndex + 1)
    fieldErrors[field] = message
  }
  return fieldErrors
}

export interface EditDialogProps<T> {
  object?: T | null
  isCreating: boolean
  onSuccess: (createdObject?: T) => void
  onError: (error: any) => void
  onClose: () => void
  service?: any
  formData: any
  getFormFields: (errors?: Record<string, string>) => React.ReactNode
  getTitle: () => string
  validateForm?: (formData: any) => string[]
  mapServerFieldErrors?: (fieldErrors: Record<string, string>) => Record<string, string>
  canEdit?: boolean
}

export const EditDialog = <T extends any>({
  object,
  isCreating,
  onSuccess,
  onError,
  onClose,
  service,
  formData,
  getFormFields,
  getTitle,
  validateForm,
  mapServerFieldErrors,
  canEdit = true,
}: EditDialogProps<T>) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate form
    if (validateForm) {
      const validationErrors = validateForm(formData)
      if (validationErrors.length > 0) {
        setErrors(parseValidationErrors(validationErrors))
        return
      }
    }

    setLoading(true)
    setErrors({})
    try {
      // Prepare data for backend - transform permissions structure
      const dataToSave = { ...formData }

      // If set_permissions exists in formData, it means we have permissions to save
      if (dataToSave.set_permissions) {
        // Backend expects owner and set_permissions at root level
        dataToSave.owner = dataToSave.owner || null
        // set_permissions is already in the right place
      }

      let result
      if (isCreating) {
        result = await service.create(dataToSave)
        toastService.showSuccess(t('successfully_created', { name: (result as any)?.name || (result as any)?.title || '' }))
      } else {
        // Use ID from formData (which includes object data) rather than object prop
        const id = (object as any)?.id || (formData as any)?.id
        result = await service.update(id, dataToSave)
        toastService.showSuccess(t('successfully_updated', { name: (result as any)?.name || (result as any)?.title || '' }))
      }
      onSuccess(result)
    } catch (error: any) {
      const { fieldErrors, errorResponse } = parseBackendErrors(error, t, t('error_saving'))

      setErrors(mapServerFieldErrors ? mapServerFieldErrors(fieldErrors) : fieldErrors)
      toastService.showError(t('error_saving'), errorResponse)

      if (!error.response?.data) {
        onError(error)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal show={true} onHide={onClose} backdrop="static" size="lg" className="edit-dialog">
      <form
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT' && (e.target as HTMLInputElement).type !== 'submit') {
            e.stopPropagation()
          }
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {getTitle()}
            {object && (object as any).id && <span className="badge bg-primary text-primary-text-contrast ms-2">ID: {(object as any).id}</span>}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {errors._general && (
            <div className="alert alert-danger mb-3" role="alert">
              {errors._general}
            </div>
          )}
          {getFormFields(errors)}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={loading || (!isCreating && !canEdit)}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                {t('saving')}
              </>
            ) : (
              t('save')
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

// Helper components for form fields
export const FormField: React.FC<{
  label: string | React.ReactNode
  error?: string
  children: React.ReactNode
  required?: boolean
}> = ({ label, error, children, required }) => (
  <div className="mb-3">
    <Form.Label>
      {label}
      {required && <span className="text-danger ms-1">*</span>}
    </Form.Label>
    {children}
    {error && <div className="text-danger small mt-1">{error}</div>}
  </div>
)

export const TextInput: React.FC<{
  value: string
  onChange: (value: string) => void
  placeholder?: string
  error?: string
  required?: boolean
  autoComplete?: string
  type?: string
  disabled?: boolean
}> = ({ value, onChange, placeholder, error, required, autoComplete, type = 'text', disabled = false }) => (
  <Form.Control
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' && e.currentTarget.tagName !== 'TEXTAREA') {
        e.stopPropagation()
      }
    }}
    placeholder={placeholder}
    isInvalid={!!error}
    required={required}
    autoComplete={autoComplete}
    disabled={disabled}
  />
)

export const ColorInput: React.FC<{
  value: string
  onChange: (value: string) => void
  error?: string
}> = ({ value, onChange, error }) => {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = React.useState(value || '#000000')
  const [showPicker, setShowPicker] = React.useState(false)
  const targetRef = React.useRef(null)
  const { randomColor } = require('../../utils/color')
  const { SliderPicker } = require('react-color')
  const { Overlay, Popover } = require('react-bootstrap')

  React.useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '#000000')
    }
  }, [value])

  const handleColorChange = (newColor: string) => {
    setInputValue(newColor)
    onChange(newColor)
  }

  const handlePickerChange = (color: any) => {
    handleColorChange(color.hex)
  }

  const handleRandomize = () => {
    const newColor = randomColor()
    handleColorChange(newColor)
  }

  const togglePicker = () => {
    setShowPicker(!showPicker)
  }

  return (
    <>
      <div className="input-group">
        <button
          ref={targetRef}
          type="button"
          className="input-group-text"
          style={{ backgroundColor: inputValue, cursor: 'pointer' }}
          aria-label="Color preview"
          onClick={togglePicker}
        >
          &nbsp;&nbsp;&nbsp;
        </button>
        <Form.Control
          type="text"
          value={inputValue}
          onChange={(e) => handleColorChange(e.target.value)}
          placeholder="#000000"
          isInvalid={!!error}
          style={{ fontFamily: 'monospace' }}
        />
        <button className="btn btn-outline-secondary" type="button" onClick={handleRandomize} title={t('random_color')}>
          <i className="bi bi-dice-5"></i>
        </button>
      </div>
      <Overlay show={showPicker} target={targetRef.current} placement="bottom" rootClose onHide={() => setShowPicker(false)}>
        <Popover id="color-picker-popover-edit" className="shadow">
          <Popover.Body style={{ minWidth: '200px', padding: '12px' }}>
            <SliderPicker color={inputValue} onChangeComplete={handlePickerChange} />
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  )
}

export const SelectInput: React.FC<{
  value: any
  onChange: (value: any) => void
  options: Array<{ value: any; label: string }>
  placeholder?: string
  error?: string
  allowNull?: boolean
  disabled?: boolean
}> = ({ value, onChange, options, placeholder, error, allowNull, disabled = false }) => {
  const { t } = useTranslation()

  const handleChange = (selectedOption: any) => {
    const selectedValue = selectedOption ? selectedOption.value : null
    onChange(selectedValue)
  }

  const getValue = () => {
    if (value === undefined || value === null) return null
    return options.find((opt) => opt.value === value) || null
  }

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderColor: error ? 'var(--bs-danger)' : state.isFocused ? 'var(--bs-primary)' : 'var(--bs-border-color)',
      boxShadow: error
        ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
        : state.isFocused
          ? `0 0 0 0.25rem hsla(var(--pngx-primary), var(--pngx-primary-lightness), var(--pngx-focus-alpha))`
          : 'none',
      backgroundColor: 'var(--bs-body-bg)',
      minHeight: '38px',
      '&:hover': {
        borderColor: error ? 'var(--bs-danger)' : 'var(--bs-primary)',
      },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: '2px 8px',
      backgroundColor: 'var(--bs-body-bg)',
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'var(--bs-body-color)',
      backgroundColor: 'var(--bs-body-bg)',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'var(--bs-body-color)',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: 'var(--bs-secondary-color)',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--bs-body-bg)',
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--bs-border-color)',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: 'var(--bs-secondary-color)',
      '&:hover': {
        color: 'var(--bs-body-color)',
      },
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      color: 'var(--bs-secondary-color)',
      '&:hover': {
        color: 'var(--bs-body-color)',
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--bs-body-bg)',
      border: '1px solid var(--bs-border-color)',
      boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
      zIndex: 1055,
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 1055,
    }),
    menuList: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--bs-body-bg)',
      padding: 0,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'var(--bs-primary)' : state.isFocused ? 'var(--bs-secondary-bg)' : 'var(--bs-body-bg)',
      color: state.isSelected ? 'var(--pngx-primary-text-contrast)' : 'var(--bs-body-color)',
      padding: '0.5rem 0.75rem',
      '&:hover': {
        backgroundColor: state.isSelected ? 'var(--bs-primary)' : 'var(--bs-secondary-bg)',
      },
    }),
  }

  return (
    <div className={`docdot-input-select ${error ? 'is-invalid' : ''}`}>
      <Select
        options={options}
        value={getValue()}
        onChange={handleChange}
        placeholder={placeholder || t('select_option')}
        className="react-select-container"
        classNamePrefix="react-select"
        isClearable={allowNull}
        isSearchable={false}
        isDisabled={disabled}
        styles={customStyles}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  )
}

export const CheckboxInput: React.FC<{
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  hint?: string
  error?: string
}> = ({ checked, onChange, label, hint, error }) => (
  <div>
    <Form.Check
      type="checkbox"
      id={`checkbox-${Math.random()}`}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      label={label}
      isInvalid={!!error}
    />
    {hint && <Form.Text className="text-muted">{hint}</Form.Text>}
  </div>
)

export const TextareaInput = React.forwardRef<
  HTMLTextAreaElement,
  {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    error?: string
    required?: boolean
    rows?: number
    monospace?: boolean
  }
>(({ value, onChange, placeholder, error, required, rows = 3, monospace = false }, ref) => (
  <Form.Control
    ref={ref}
    as="textarea"
    value={value}
    onChange={(e) => onChange((e.target as HTMLTextAreaElement).value)}
    onKeyDown={(e) => {
      e.stopPropagation()
    }}
    placeholder={placeholder}
    isInvalid={!!error}
    required={required}
    rows={rows}
    style={monospace ? { fontFamily: 'monospace' } : undefined}
  />
))

TextareaInput.displayName = 'TextareaInput'

export const HorizontalFormField: React.FC<{
  label: string
  error?: string
  children: React.ReactNode
  required?: boolean
  className?: string
}> = ({ label, error, children, required, className = '' }) => (
  <div className={`mb-3 ${className}`}>
    <Form.Label className="form-label-horizontal">
      {label}
      {required && <span className="text-danger ms-1">*</span>}
    </Form.Label>
    {children}
    {error && <div className="text-danger small mt-1">{error}</div>}
  </div>
)
